import * as React from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import { GatsbyImage, getImage, IGatsbyImageData } from "gatsby-plugin-image";
import { trackPhoneClick, trackBookingClick } from "../utils/tracking";

type HeaderQuery = {
  contentfulComponentHeader: {
    h1: string | null;
    h2: string | null;
    logo: {
      title: string | null;
      url: string | null;
      mimeType: string;
      gatsbyImageData: IGatsbyImageData | null;
    } | null;
  } | null;
  site: {
    siteMetadata: {
      headerTitle: string | null;
      headerSubtitle: string | null;
    };
  };
  allContentfulPage: {
    nodes: {
      slug: string;
      title: string;
      contentfulparent: { slug: string } | null;
    }[];
  };
};

const MENU: { to: string; label: string }[] = [
  { to: "/", label: "Home" },
  { to: "/about-me/", label: "About Me" },
  { to: "/services/", label: "Services" },
  { to: "/media/", label: "Media" },
  { to: "/contact/", label: "Contact" },
];

// Matches an Australian phone number (a leading 0 followed by 9 more digits,
// e.g. "0416 631 133"). Short numbers in the address ("133", "2010") won't match.
const PHONE_RE = /(0\d(?:[\s-]?\d){8})/;

// Renders the header subtitle, wrapping any phone number in a tel: link.
const renderSubtitle = (text: string): React.ReactNode => {
  const match = text.match(PHONE_RE);
  if (!match) return text;
  const phone = match[1];
  const [before, after] = text.split(phone);
  return (
    <>
      {before}
      <a
        href={`tel:${phone.replace(/[\s-]/g, "")}`}
        onClick={() => trackPhoneClick(phone, "header")}
      >
        {phone}
      </a>
      {after}
    </>
  );
};

const Header: React.FC = () => {
  // Mobile menu (hamburger) open state — applied directly to the nav/button via
  // React className so it works reliably (no imperative classList timing).
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    // Scroll-collapse only runs on desktop; mobile uses the static bar + menu.
    if (window.matchMedia("(max-width: 1024px)").matches) return;

    const header = document.getElementById("site-header");
    if (!header) return;

    const root = document.documentElement;
    const body = document.body;
    const branding = header.querySelector<HTMLElement>("#site-branding");
    const nav = header.querySelector<HTMLElement>("#site-nav");
    const animated = [header, branding, nav].filter(
      (el): el is HTMLElement => el !== null
    );

    const measure = () => {
      animated.forEach((el) => (el.style.transition = "none"));
      const wasSmall = header.classList.contains("small");

      header.classList.remove("small");
      void header.offsetHeight;
      const largeH = header.offsetHeight;

      header.classList.add("small");
      void header.offsetHeight;
      const smallH = header.offsetHeight;

      header.classList.toggle("small", wasSmall);
      void header.offsetHeight;

      root.style.setProperty("--site-header-h-large", `${largeH}px`);
      root.style.setProperty("--site-header-h-small", `${smallH}px`);

      requestAnimationFrame(() => {
        animated.forEach((el) => (el.style.transition = ""));
      });
    };

    measure();
    window.addEventListener("resize", measure);

    const COLLAPSE_AT = 80;
    const EXPAND_AT = 20;
    const MIN_SCROLL_ROOM_WHEN_SMALL = 40;

    const swapClasses = () => {
      const y = window.scrollY;
      const isSmall = header.classList.contains("small");

      if (isSmall) {
        if (y < EXPAND_AT) {
          header.classList.remove("small");
          body.classList.remove("header-small");
        }
        return;
      }

      if (y <= COLLAPSE_AT) return;

      const largeH =
        parseFloat(root.style.getPropertyValue("--site-header-h-large")) || 0;
      const smallH =
        parseFloat(root.style.getPropertyValue("--site-header-h-small")) || 0;
      const delta = largeH - smallH;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll - delta < MIN_SCROLL_ROOM_WHEN_SMALL) return;

      header.classList.add("small");
      body.classList.add("header-small");
    };

    document.addEventListener("scroll", swapClasses, { passive: true });
    swapClasses();
    return () => {
      document.removeEventListener("scroll", swapClasses);
      window.removeEventListener("resize", measure);
      body.classList.remove("header-small");
      root.style.removeProperty("--site-header-h-large");
      root.style.removeProperty("--site-header-h-small");
    };
  }, []);

  const data = useStaticQuery<HeaderQuery>(graphql`
    query HeaderData {
      contentfulComponentHeader {
        h1
        h2
        logo {
          title
          url
          mimeType
          gatsbyImageData(
            width: 100
            height: 100
            layout: FIXED
            quality: 95
            placeholder: NONE
            formats: [AUTO, WEBP]
            outputPixelDensities: [1, 2, 3]
          )
        }
      }
      site {
        siteMetadata {
          headerTitle
          headerSubtitle
        }
      }
      allContentfulPage(sort: { title: ASC }) {
        nodes {
          slug
          title
          contentfulparent {
            slug
          }
        }
      }
    }
  `);

  const header = data.contentfulComponentHeader;
  const meta = data.site.siteMetadata;
  const h1Text = header?.h1 ?? meta.headerTitle ?? "Dr Peter Dayman";
  const h2Text = header?.h2 ?? meta.headerSubtitle ?? "";
  const logo = header?.logo ?? null;
  const logoImage = logo?.gatsbyImageData
    ? getImage(logo.gatsbyImageData)
    : null;
  const logoAlt = logo?.title ?? h1Text;

  const allPages = data.allContentfulPage.nodes;
  const services = allPages.filter(
    (n) => n.contentfulparent?.slug === "services"
  );

  // Map each slug to its immediate parent so links can include the full
  // ancestor chain (matches gatsby-node's page paths).
  const parentBySlug = new Map<string, string | null>();
  allPages.forEach((n) => {
    if (n.slug) parentBySlug.set(n.slug, n.contentfulparent?.slug ?? null);
  });
  const buildPath = (slug: string): string => {
    const segments: string[] = [];
    let current: string | null = slug;
    const seen = new Set<string>();
    while (current && !seen.has(current)) {
      seen.add(current);
      segments.unshift(current);
      current = parentBySlug.get(current) ?? null;
    }
    return `/${segments.join("/")}/`;
  };
  const childrenOf = (slug: string) =>
    allPages.filter((n) => n.contentfulparent?.slug === slug);


  return (
    <header id="site-header">
      <nav
        id="site-nav"
        className={menuOpen ? "is-open" : undefined}
        onClick={(e) => {
          // Close the mobile menu after a link is tapped.
          if ((e.target as HTMLElement).closest("a")) setMenuOpen(false);
        }}
      >
        {logo?.url && (
          <div id="site-sm-logo" aria-hidden="true">
            <img src={logo.url} alt="" />
          </div>
        )}
        <ul>
          {MENU.map(({ to, label }) => {
            const isServices = to === "/services/";
            const isHome = to === "/";
            const onClick = isHome
              ? (e: React.MouseEvent) => {
                  if (window.location.pathname === "/") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }
              : undefined;
            if (!isServices || services.length === 0) {
              return (
                <li key={to}>
                  <Link to={to} onClick={onClick}>
                    {label}
                  </Link>
                </li>
              );
            }
            return (
              <li key={to} className="has-dropdown">
                <Link to={to}>
                  {label}
                  <svg
                    className="dropdown-caret"
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M1 1l4 4 4-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
                <ul className="dropdown">
                  {services.map(({ slug, title }) => {
                    const children = childrenOf(slug);
                    if (children.length === 0) {
                      return (
                        <li key={slug}>
                          <Link to={buildPath(slug)}>{title}</Link>
                        </li>
                      );
                    }
                    return (
                      <li key={slug} className="has-subdropdown">
                        <Link to={buildPath(slug)}>
                          {title}
                          <svg
                            className="dropdown-caret"
                            width="10"
                            height="6"
                            viewBox="0 0 10 6"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              d="M1 1l4 4 4-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Link>
                        <ul className="subdropdown">
                          {children.map((child) => (
                            <li key={child.slug}>
                              <Link to={buildPath(child.slug)}>
                                {child.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
        <a
          id="site-book-cta"
          href="https://www.corepractice.is/practices/macleaydental/macleay-dental/peter-dayman#/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackBookingClick}
        >
          Make a booking
        </a>
      </nav>
      <div id="site-branding">
        <div id="site-brand-row">
          {logoImage ? (
            <Link to="/" id="site-logo-link" aria-label="Home">
              <GatsbyImage image={logoImage} alt={logoAlt} id="site-logo" />
            </Link>
          ) : logo?.url ? (
            <Link to="/" id="site-logo-link" aria-label="Home">
              <img src={logo.url} alt={logoAlt} id="site-logo" />
            </Link>
          ) : null}
          <h1 id="site-title">{h1Text}</h1>
        </div>
        {h2Text && <h2 id="site-subtitle">{renderSubtitle(h2Text)}</h2>}
        <button
          id="site-menu-toggle"
          className={menuOpen ? "is-open" : undefined}
          type="button"
          aria-label="Menu"
          aria-controls="site-nav"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="hamburger-bar" aria-hidden="true" />
          <span className="hamburger-bar" aria-hidden="true" />
          <span className="hamburger-bar" aria-hidden="true" />
        </button>
      </div>
    </header>
  );
};

export default Header;
